import { createCryptoCurrency } from '@0xtorch/core'
import { kaia as viemKaia } from 'viem/chains'
import { type Client, createClient } from '../../client'
import type { Explorer } from '../../explorer'
import { createKaiascan } from '../../explorer'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'

/**
 * Kaia のネイティブトークンとして利用する KAIA 通貨定義。
 */
const kaia = createCryptoCurrency({
  type: 'CryptoCurrency',
  id: 'kaia',
  name: 'Kaia',
  symbol: 'KAIA',
  market: {
    coingeckoId: 'kaia',
  },
  updatedAt: 0,
})

/**
 * Kaia Mainnet の標準設定を作成する。
 *
 * @param params - Explorer 接続設定
 * @param params.explorerApiKey - 任意の Kaiascan API key
 * @param params.explorerProxyUrl - Explorer API を中継するプロキシ URL
 * @param params.explorerPageSize - Explorer account 系 API のページサイズ
 * @returns Kaia Mainnet 定義
 */
export const createKaiaChain = ({
  explorerApiKey: apiKey,
  explorerProxyUrl: proxyUrl,
  explorerPageSize: pageSize,
}: {
  explorerApiKey?: string
  explorerProxyUrl?: string
  explorerPageSize?: number
}): Chain =>
  createKaiaChainCustom({
    client: createClient({ chain: viemKaia, httpRpcs: kaiaHttpRpcs }),
    explorer: createKaiascan({
      name: 'Kaiascan',
      baseUrl: 'https://kaiascan.io',
      apiBaseUrl: 'https://mainnet-oapi.kaiascan.io/api',
      oapiBaseUrl: 'https://mainnet-oapi.kaiascan.io',
      apiKey,
      proxyUrl,
      pageSize,
    }),
  })

type CreateKaiaChainCustomParameters = {
  client: Client
  explorer: Explorer
}

/**
 * Kaia Mainnet の client と explorer を差し替えて作成する。
 *
 * @param params - Kaia Mainnet の依存実装
 * @param params.client - EVM RPC client
 * @param params.explorer - Explorer 実装
 * @returns Kaia Mainnet 定義
 */
export const createKaiaChainCustom = ({
  client,
  explorer,
}: CreateKaiaChainCustomParameters): Chain => ({
  id: 8217,
  network: 'kaia',
  name: 'Kaia',
  nativeToken: {
    name: 'Kaia',
    symbol: 'KAIA',
    decimals: 18,
    currency: kaia,
  },
  wrappedTokenAddresses: new Set([
    '0x19aac5f612f524b754ca7e7c41cbfa2e981a4432',
    '0xe4f05a66ec68b54a58b17c22107b02e0232cc817',
  ]),
  explorer,
  client,
  blockTime: 1_000,
  coingeckoId: 'klay-token',
  defillamaId: undefined,
})

/**
 * Kaia Mainnet の HTTP RPC 候補。
 */
export const kaiaHttpRpcs: HttpRpc[] = [
  {
    url: 'https://public-en.node.kaia.io',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 1_000n,
  },
  {
    url: 'https://kaia.api.pocket.network',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 1_000n,
  },
  {
    url: 'https://rpc.ankr.com/kaia',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 1_000n,
  },
  {
    url: 'https://kaia-mainnet.gateway.tatum.io',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 1_000n,
  },
]

/**
 * Kaia Mainnet の WebSocket RPC 候補。
 */
export const kaiaWebsocketRpcUrls: readonly string[] = ['wss://klaytn.drpc.org']
